import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchUsers, fetchRoles, createUser, updateUser, deleteUser,
  fetchMetaZBMs, fetchUserAssignments, updateUserAssignments, resendAccessLink,
} from '../services/authService';
import {
  Plus, Search, Edit2, Trash2, X, Check, ChevronLeft, ChevronRight,
  UserCheck, UserX, RefreshCw, Building2, RefreshCcw, Send,
} from 'lucide-react';
import UserAssignmentModal from './UserAssignmentModal';
import CsvSyncModal       from './CsvSyncModal';

// ─── Role badge colours ───────────────────────────────────────────────────────
const ROLE_STYLES = {
  superadmin:  'bg-purple-100 text-purple-700',
  stakeholder: 'bg-orange-100 text-orange-700',
  sales:       'bg-blue-100   text-blue-700',
  physician:   'bg-emerald-100 text-emerald-700',
};

// ─── Empty form state ─────────────────────────────────────────────────────────
const EMPTY_FORM = { name: '', email: '', role: 'sales', sub_type: '', zbmId: '' };

// ─── Sub-type badge styles (for sales users) ─────────────────────────────────
const SUBTYPE_STYLES = {
  'ZBM':                  'bg-indigo-50 text-indigo-700 border border-indigo-200',
  'Sales Representative': 'bg-sky-50 text-sky-700 border border-sky-200',
  'Medical Advisor':      'bg-teal-50 text-teal-700 border border-teal-200',
};

export default function UserManagement() {
  // List state
  const [users, setUsers]         = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [search, setSearch]       = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [roles, setRoles]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [listError, setListError] = useState('');

  // Modal state
  const [modal, setModal]         = useState(null); // null | 'create' | 'edit' | 'delete'
  const [selected, setSelected]   = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving]       = useState(false);
  const [emailConfirmed, setEmailConfirmed] = useState(false); // checkbox: superadmin confirms email is valid
  const [resendingId, setResendingId] = useState(null);
  const [recentlySentIds, setRecentlySentIds] = useState(new Set()); // user IDs on client-side cooldown
  const [notice, setNotice] = useState('');

  // Assignment modal state
  const [assignUser, setAssignUser] = useState(null);
  // CSV Sync modal state
  const [showSync, setShowSync] = useState(false);
  // ZBM list for "Reports to" dropdown
  const [zbmList, setZbmList] = useState([]);

  // ─── Load users ─────────────────────────────────────────────────────────────
  const loadUsers = useCallback(async (page = 1) => {
    setLoading(true);
    setListError('');
    try {
      const data = await fetchUsers({ page, limit: pagination.limit, search, role: roleFilter });
      setUsers(data.users);
      setPagination(data.pagination);
    } catch (err) {
      setListError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, pagination.limit]);

  useEffect(() => { loadUsers(1); }, [search, roleFilter]); // eslint-disable-line

  useEffect(() => {
    fetchRoles().then(setRoles).catch(() => {});
    // Load ZBM list for "Reports to" dropdown
    fetchMetaZBMs().then(setZbmList).catch(() => {});
  }, []);

  // ─── Modal helpers ───────────────────────────────────────────────────────────
  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError('');
    setEmailConfirmed(false);
    setModal('create');
  };
  const openEdit   = (u) => {
    setSelected(u);
    setForm({ name: u.name, email: u.email, role: u.role, sub_type: u.sub_type || '' });
    setFormError('');
    setModal('edit');
  };
  const openDelete = (u) => { setSelected(u); setModal('delete'); };
  const closeModal = () => { setModal(null); setSelected(null); setFormError(''); setEmailConfirmed(false); };

  // ─── Form submit ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setFormError('');
    setSaving(true);
    try {
      if (modal === 'create') {
        const newUser = await createUser(form);
        // If Sales Rep with a ZBM assigned — create reporting relationship automatically
        if (form.role === 'sales' && form.sub_type === 'Sales Representative' && form.zbmId && newUser?.id) {
          try {
            const zbmAssignments = await fetchUserAssignments(form.zbmId);
            const currentReports = (zbmAssignments.reports || []).map(r => r.id);
            // Only add if not already in the list
            if (!currentReports.includes(newUser.id)) {
              await updateUserAssignments(form.zbmId, {
                reports: [...currentReports, newUser.id],
              });
            }
          } catch (e) {
            console.warn('Could not link Sales Rep to ZBM:', e.message);
          }
        }
      } else {
        // Password intentionally excluded from edit — user must use forgot password
        const payload = { name: form.name, email: form.email, role: form.role, sub_type: form.sub_type || null };
        await updateUser(selected.id, payload);
      }
      closeModal();
      loadUsers(pagination.page);
    } catch (err) {
      setFormError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteUser(selected.id);
      closeModal();
      loadUsers(pagination.page);
    } catch (err) {
      setFormError(err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const handleResendLink = async (u) => {
    // Guard: ignore clicks while a send is in flight or the user is on cooldown
    if (resendingId === u.id || recentlySentIds.has(u.id)) return;

    setResendingId(u.id);
    setNotice('');
    setListError('');
    try {
      const result = await resendAccessLink(u.id);
      setNotice(result?.message || `A set-password link has been sent to ${u.email}`);

      // Put this user on a 2-minute client-side cooldown so the button can't
      // be clicked again to spam their inbox (matches the backend cooldown)
      setRecentlySentIds(prev => new Set(prev).add(u.id));
      setTimeout(() => {
        setRecentlySentIds(prev => {
          const next = new Set(prev);
          next.delete(u.id);
          return next;
        });
      }, 2 * 60 * 1000);

      loadUsers(pagination.page); // onboarding state may have changed
    } catch (err) {
      setListError(err.message || 'Could not send the link');
    } finally {
      setResendingId(null);
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await updateUser(u.id, { is_active: !u.is_active });
      loadUsers(pagination.page);
    } catch (err) {
      setListError(err.message);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            {pagination.total} total users
          </p>
        </div>
        <div className="flex items-center space-x-2 md:flex-shrink-0">
          <button
            onClick={() => setShowSync(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            title="Sync new physicians from CSV"
          >
            <RefreshCcw className="w-4 h-4" />
            <span>Sync from CSV</span>
          </button>
          <button
            onClick={openCreate}
            className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add User</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            autoComplete="off"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <select
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">All Roles</option>
          {roles.map(r => (
            <option key={r.id} value={r.name}>{r.name}</option>
          ))}
        </select>
        <button
          onClick={() => loadUsers(pagination.page)}
          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Error */}
      {listError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
          {listError}
        </div>
      )}

      {/* Confirmation after resending a set-password link */}
      {notice && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700 flex items-center justify-between">
          <span className="flex items-center space-x-2">
            <Check className="w-4 h-4 flex-shrink-0" />
            <span>{notice}</span>
          </span>
          <button onClick={() => setNotice('')} className="text-emerald-600 hover:text-emerald-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Email</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Role</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Created</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-400">
                    <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-400">No users found</td>
                </tr>
              ) : users.map(u => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900">{u.name}</td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell max-w-[180px] truncate">{u.email}</td>
                  <td className="px-4 py-3">
                    {u.role === 'sales' && u.sub_type ? (
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_STYLES['sales']}`}>
                        Sales → {u.sub_type}
                      </span>
                    ) : (
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${ROLE_STYLES[u.role] || 'bg-gray-100 text-gray-600'}`}>
                        {u.role}
                      </span>
                    )}
                  </td>
                    <td className="px-4 py-3">
                      {u.role === 'superadmin' ? (
                        // Superadmins: read-only badge — cannot be deactivated
                        <span className="inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          <span>Active</span>
                        </span>
                      ) : (
                        <button
                          onClick={() => handleToggleActive(u)}
                          title={u.is_active ? 'Click to Deactivate' : 'Click to Activate'}
                          className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                            u.is_active
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                              : 'bg-red-50 text-red-600 border-red-200 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200'
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${u.is_active ? 'bg-emerald-500' : 'bg-red-400'}`} />
                          <span>{u.is_active ? 'Active' : 'Inactive'}</span>
                        </button>
                      )}
                    </td>
                  <td className="px-4 py-3 text-gray-500 hidden lg:table-cell">
                    {new Date(u.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end space-x-1">
                        {/* Assignments button — sales and physician only */}
                        {(u.role === 'sales' || u.role === 'physician') && (
                          <button
                            onClick={() => setAssignUser(u)}
                            className="p-1.5 rounded hover:bg-emerald-50 transition-colors"
                            title="Manage Hospital & Zone Assignments"
                          >
                            <Building2 className="w-4 h-4 text-gray-400 hover:text-emerald-600" />
                          </button>
                        )}
                        {/* Resend the set-password link — only useful while the
                            account can actually be signed into */}
                        {!!u.is_active && u.role !== 'superadmin' && (
                          <button
                            onClick={() => handleResendLink(u)}
                            disabled={resendingId === u.id || recentlySentIds.has(u.id)}
                            className="p-1.5 rounded hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title={
                              recentlySentIds.has(u.id)
                                ? 'Link already sent — please wait 2 minutes before sending again'
                                : 'Email a fresh link to set their password'
                            }
                          >
                            <Send className={`w-4 h-4 ${
                              resendingId === u.id ? 'text-blue-400 animate-pulse'
                              : recentlySentIds.has(u.id) ? 'text-emerald-500'
                              : 'text-gray-400 hover:text-blue-600'
                            }`} />
                          </button>
                        )}
                        {/* Edit and delete hidden for superadmin — cannot modify superadmin accounts */}
                        {u.role !== 'superadmin' && (
                          <>
                            <button
                              onClick={() => openEdit(u)}
                              className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4 text-gray-400 hover:text-blue-500" />
                            </button>
                            <button
                              onClick={() => openDelete(u)}
                              className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                              title="Deactivate"
                            >
                              <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} users)
            </span>
            <div className="flex items-center space-x-2">
              <button
                disabled={pagination.page <= 1}
                onClick={() => loadUsers(pagination.page - 1)}
                className="p-1.5 rounded border border-gray-300 disabled:opacity-40 hover:bg-white transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => loadUsers(pagination.page + 1)}
                className="p-1.5 rounded border border-gray-300 disabled:opacity-40 hover:bg-white transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Create / Edit Modal ─────────────────────────────────────────────── */}
      {(modal === 'create' || modal === 'edit') && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {modal === 'create' ? 'Add New User' : 'Edit User'}
              </h2>
              <button onClick={closeModal} className="p-1 rounded hover:bg-gray-100">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  autoComplete="off"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Enter full name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  autoComplete="off"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Enter email address"
                />
              </div>

              {/* Passwords are never set here. A new user receives a single-use
                  link and chooses their own; an existing one uses Forgot Password. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500">
                  {modal === 'edit'
                    ? 'Password can only be changed by the user via Forgot Password'
                    : 'The user will be emailed a link to set their own password'}
                </div>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                <select
                  value={form.role}
                  onChange={e => setForm(f => ({ ...f, role: e.target.value, sub_type: '' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="superadmin">Superadmin</option>
                  <option value="stakeholder">Stakeholder</option>
                  <option value="sales">Sales</option>
                  <option value="physician">Physician</option>
                </select>
              </div>

              {/* Sub-type — only for sales role */}
              {form.role === 'sales' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sales Sub-Type</label>
                  <select
                    value={form.sub_type}
                    onChange={e => setForm(f => ({ ...f, sub_type: e.target.value, zbmId: '' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Select sub-type...</option>
                    <option value="ZBM">ZBM</option>
                    <option value="Sales Representative">Sales Representative</option>
                    <option value="Medical Advisor">Medical Advisor</option>
                  </select>
                </div>
              )}

              {/* Reports to ZBM — only for Sales Representative in create mode */}
              {modal === 'create' && form.role === 'sales' && form.sub_type === 'Sales Representative' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reports to ZBM
                    <span className="text-gray-400 font-normal ml-1">(optional)</span>
                  </label>
                  <select
                    value={form.zbmId}
                    onChange={e => setForm(f => ({ ...f, zbmId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Select ZBM (optional)...</option>
                    {zbmList.map(zbm => (
                      <option key={zbm.id} value={zbm.id}>{zbm.name}</option>
                    ))}
                  </select>
                  {form.zbmId && (
                    <p className="text-xs text-blue-600 mt-1">
                      ✓ This Sales Rep will be automatically added under the selected ZBM
                    </p>
                  )}
                </div>
              )}

              {formError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {formError}
                </p>
              )}

              {/* Email notice — only for create */}
              {modal === 'create' && form.email && (
                <div className={`p-3 rounded-lg border text-xs leading-relaxed ${
                  emailConfirmed
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : 'bg-amber-50 border-amber-200 text-amber-800'
                }`}>
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailConfirmed}
                      onChange={e => setEmailConfirmed(e.target.checked)}
                      className="mt-0.5 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 cursor-pointer flex-shrink-0"
                    />
                    <span>
                      {emailConfirmed
                        ? <>A welcome email with login credentials <strong>will be sent</strong> to <strong>{form.email}</strong>.</>
                        : <>A welcome email with login credentials will be sent to <strong>{form.email}</strong>. Check this box to confirm this is a valid email address. If this is a temporary/dummy address, leave unchecked — the user will appear in the onboarding queue for follow-up.</>
                      }
                    </span>
                  </label>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {saving
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <Check className="w-4 h-4" />
                }
                <span>{modal === 'create' ? 'Create User' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ────────────────────────────────────────────── */}
      {modal === 'delete' && selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="px-6 py-5">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 text-center mb-2">Deactivate User</h2>
              <p className="text-sm text-gray-500 text-center">
                Are you sure you want to deactivate <span className="font-medium text-gray-700">{selected.name}</span>?
                They will lose access immediately.
              </p>
              {formError && (
                <p className="mt-3 text-sm text-red-600 text-center">{formError}</p>
              )}
            </div>
            <div className="flex items-center justify-center space-x-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {saving
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <Trash2 className="w-4 h-4" />
                }
                <span>Deactivate</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── CSV Sync Modal ──────────────────────────────────────────────────── */}
      {showSync && (
        <CsvSyncModal
          onClose={() => setShowSync(false)}
          onImported={() => loadUsers(1)}
        />
      )}

      {/* ── Assignment Modal ────────────────────────────────────────────────── */}
      {assignUser && (
        <UserAssignmentModal
          user={assignUser}
          onClose={() => setAssignUser(null)}
          onSaved={() => loadUsers(pagination.page)}
        />
      )}

    </div>
  );
}
